import * as React from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Divider,
  Fade,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const DESTRUCTIVE_TITLE_PATTERN = /eliminar|delete|remover|borrar/i;
const LOGOUT_TITLE_PATTERN = /cerrar sesión|logout|sign out|salir/i;
const LANGUAGE_TITLE_PATTERN = /idioma|language/i;

function isDestructiveAction(title, variant) {
  if (variant === "danger") return true;
  if (variant === "default") return false;
  return Boolean(title && DESTRUCTIVE_TITLE_PATTERN.test(title));
}

function ModalIcon({ title, destructive, theme }) {
  let Icon = InfoOutlinedIcon;
  let accent = theme.palette.primary.main;

  if (destructive) {
    Icon = DeleteOutlineOutlinedIcon;
    accent = theme.palette.danger.main;
  } else if (title && LOGOUT_TITLE_PATTERN.test(title)) {
    Icon = LogoutOutlinedIcon;
    accent = theme.palette.primary.main;
  } else if (title && LANGUAGE_TITLE_PATTERN.test(title)) {
    Icon = LanguageOutlinedIcon;
    accent = theme.palette.info.main;
  }

  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: alpha(accent, 0.12),
        color: accent,
      }}
    >
      <Icon sx={{ fontSize: 24 }} />
    </Box>
  );
}

export default function ModalGeneric({
  open,
  title,
  description,
  onClose,
  onAccept,
  titleComponent,
  descriptionComponent,
  cancelButtonComponent,
  acceptButtonComponent,
  bodyComponent,
  variant,
  maxWidth = 480,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const destructive = isDestructiveAction(title, variant);
  const hasDescription = Boolean(description?.trim?.() ?? description);

  const handleAccept = () => {
    onAccept?.();
  };

  const handleCancel = () => {
    onClose?.();
  };

  const buttonBaseSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    px: 2.5,
    py: 0.85,
    minWidth: 108,
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      closeAfterTransition
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.nav?.main ?? "#000922", 0.55),
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            width: "100%",
            maxWidth,
            bgcolor: "background.paper",
            borderRadius: 3,
            overflow: "hidden",
            outline: "none",
            boxShadow: `0 24px 48px ${alpha(theme.palette.nav?.main ?? "#000", 0.22)}`,
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: hasDescription || descriptionComponent || bodyComponent ? 2 : 2.5,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            {!titleComponent && <ModalIcon title={title} destructive={destructive} theme={theme} />}
            <Box sx={{ flex: 1, minWidth: 0, pt: titleComponent ? 0 : 0.25 }}>
              {titleComponent ? (
                <div>{titleComponent}</div>
              ) : (
                <Typography
                  id="modal-modal-title"
                  variant="h6"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    lineHeight: 1.35,
                  }}
                >
                  {title}
                </Typography>
              )}
              {descriptionComponent ? (
                <Box sx={{ mt: 1.5 }}>{descriptionComponent}</Box>
              ) : (
                hasDescription && (
                  <Typography
                    id="modal-modal-description"
                    variant="body1"
                    sx={{
                      mt: 1.5,
                      color: "text.secondary",
                      lineHeight: 1.55,
                    }}
                  >
                    {description}
                  </Typography>
                )
              )}
            </Box>
          </Box>

          {bodyComponent && (
            <Box sx={{ px: 3, pb: 2 }}>{bodyComponent}</Box>
          )}

          <Divider />

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            {cancelButtonComponent ? (
              <div>{cancelButtonComponent}</div>
            ) : (
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  ...buttonBaseSx,
                  color: "text.secondary",
                  borderColor: alpha(theme.palette.secondary.main, 0.45),
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.06),
                  },
                }}
              >
                {t("buttons.cancel")}
              </Button>
            )}
            {acceptButtonComponent ? (
              <div>{acceptButtonComponent}</div>
            ) : (
              <Button
                variant="contained"
                onClick={handleAccept}
                disableElevation
                sx={{
                  ...buttonBaseSx,
                  bgcolor: destructive
                    ? theme.palette.danger.main
                    : theme.palette.primary.main,
                  "&:hover": {
                    bgcolor: destructive
                      ? theme.palette.danger.dark ?? "#d94343"
                      : theme.palette.primary.dark ?? "#053a73",
                  },
                }}
              >
                {t("buttons.accept")}
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
